import React, { useEffect, useState } from 'react';
import api, { isSupervisorRole, getCurrentUser } from '../api/expoApi';
import HubSelector from '../components/HubSelector';

export default function OperationsDashboard() {
    const user = getCurrentUser();
    const isSupervisor = isSupervisorRole(user?.role);
    const [hubId, setHubId] = useState(isSupervisor ? null : (user?.hub_id || user?.hubId));
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [kpiData, setKpiData] = useState([]);
    const [cashSummary, setCashSummary] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [unreadAlerts, setUnreadAlerts] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = { date };
            if (hubId) params.hub_id = hubId;
            const [k, c, ls, ac] = await Promise.all([
                api.get('/kpis/dashboard', { params }),
                api.get('/cash/summary', { params }),
                api.get('/inventory/low-stock'),
                api.get('/alerts/unread-count')
            ]);
            setKpiData(k.data?.data || []);
            setCashSummary(c.data?.data || []);
            setLowStock(ls.data?.data || []);
            setUnreadAlerts(ac.data?.data?.count || 0);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [hubId, date]);

    const totalIn = cashSummary.reduce((s, h) => s + Number(h.total_in || 0), 0);
    const totalOut = cashSummary.reduce((s, h) => s + Number(h.total_out || 0), 0);
    const totalCod = cashSummary.reduce((s, h) => s + Number(h.cod_collected || 0), 0);

    return (
        <div className="ops-dashboard" style={{ padding: 24 }}>
            <h1>Operations Dashboard</h1>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
                <HubSelector value={hubId} onChange={setHubId} />
                <div>
                    <label>Date: </label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <button onClick={load}>Refresh</button>
                {loading && <span style={{ color: '#888' }}>Loading...</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
                <KpiCard label="Cash In" value={`PKR ${totalIn.toLocaleString()}`} color="#198754" />
                <KpiCard label="Cash Out" value={`PKR ${totalOut.toLocaleString()}`} color="#dc3545" />
                <KpiCard label="COD Collected" value={`PKR ${totalCod.toLocaleString()}`} color="#0d6efd" />
                <KpiCard label="Low Stock Items" value={lowStock.length} color={lowStock.length ? '#fd7e14' : '#6c757d'} />
                <KpiCard label="Unread Alerts" value={unreadAlerts} color={unreadAlerts ? '#dc3545' : '#6c757d'} />
            </div>

            <h2>System KPIs ({date})</h2>
            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead><tr>
                        <th>KPI</th><th>Hub</th><th>Actual</th><th>Target</th><th>Achievement</th>
                    </tr></thead>
                    <tbody>
                        {kpiData.length === 0 && (
                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: 16 }}>
                                No data — run auto-compute or record entries
                            </td></tr>
                        )}
                        {kpiData.map((k, i) => (
                            <tr key={i}>
                                <td>{k.name}</td>
                                <td>{k.hub_code || '—'}</td>
                                <td>{k.actual_value !== null ? Number(k.actual_value).toLocaleString() : '—'} {k.unit}</td>
                                <td>{k.target_value !== null ? Number(k.target_value).toLocaleString() : '—'}</td>
                                <td>
                                    {k.achievement_pct !== null ? (
                                        <span style={{
                                            color: Number(k.achievement_pct) >= 100 ? '#198754'
                                                 : Number(k.achievement_pct) >= 75 ? '#fd7e14' : '#dc3545',
                                            fontWeight: 600
                                        }}>{k.achievement_pct}%</span>
                                    ) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h2 style={{ marginTop: 32 }}>Cash Position by Hub</h2>
            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead><tr>
                        <th>Hub</th><th>Cash In</th><th>Cash Out</th><th>COD</th><th>Transactions</th>
                    </tr></thead>
                    <tbody>
                        {cashSummary.length === 0 && (
                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: 16 }}>
                                No transactions for {date}
                            </td></tr>
                        )}
                        {cashSummary.map((h, i) => (
                            <tr key={i}>
                                <td>{h.hub_code} — {h.hub_name}</td>
                                <td style={{ color: '#198754' }}>PKR {Number(h.total_in).toLocaleString()}</td>
                                <td style={{ color: '#dc3545' }}>PKR {Number(h.total_out).toLocaleString()}</td>
                                <td>PKR {Number(h.cod_collected).toLocaleString()}</td>
                                <td>{h.transaction_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {lowStock.length > 0 && (
                <>
                    <h2 style={{ marginTop: 32, color: '#fd7e14' }}>⚠ Low Stock Alerts</h2>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={tableStyle}>
                            <thead><tr><th>SKU</th><th>Product</th><th>Category</th><th>Current</th><th>Threshold</th></tr></thead>
                            <tbody>
                                {lowStock.map((p, i) => (
                                    <tr key={i}>
                                        <td><code>{p.sku}</code></td>
                                        <td>{p.name}</td>
                                        <td>{p.category}</td>
                                        <td style={{ color: '#dc3545', fontWeight: 600 }}>{p.current_qty}</td>
                                        <td>{p.low_stock_threshold}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function KpiCard({ label, value, color }) {
    return (
        <div style={{
            background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16,
            borderLeft: `4px solid ${color}`
        }}>
            <div style={{ color: '#666', fontSize: '.85rem' }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        </div>
    );
}

const tableStyle = {
    width: '100%', borderCollapse: 'collapse', background: '#fff',
    border: '1px solid #e0e0e0', borderRadius: 4
};
